/**
 * Rótulo da causa exibido no botão "Tipo de incentivo" do simulador (AUC-125).
 *
 * Antes o rótulo vinha do PERCENTUAL, não da causa: 7% => "Esporte", qualquer outro
 * => "Cultura/Social". Campanha de conselho (CMDCA) caía no balde "Cultura/Social",
 * que é o que o cliente da VIA Solidária apontou -- a campanha é Estatuto da Criança
 * e Adolescente, nada de cultura.
 *
 * A regra acordada: o nome sai da LEI DE INCENTIVO dos projetos da campanha, que já
 * está cadastrada. Nada de texto customizado por campanha.
 *   - todos os projetos na mesma lei -> o rótulo curto dessa lei
 *   - leis diferentes               -> nome genérico com as causas presentes
 */

// Lei única: o nome curto da própria lei. É o que o cliente reconhece -- "Criança e
// Adolescente" é o nome da lei do projeto, não um apelido que inventamos.
export const INCENTIVE_LAW_SHORT_LABEL = {
  1: "Criança e Adolescente", // Estatuto de Criança e Adolescente
  2: "Idoso",                 // Estatuto do Idoso
  3: "Esporte",               // Lei Federal do Esporte
  4: "Cultura"                // Lei de Incentivo à Cultura - Rouanet
};

// Mais de uma lei: não dá para citar as quatro, então agrupa. ECA e Idoso são as duas
// leis de conselho (fundo municipal), que o material da AUC sempre chamou de "Social".
export const INCENTIVE_LAW_GROUP = { 1: "Social", 2: "Social", 3: "Esporte", 4: "Cultura" };

// Ordem de exibição do caso misto, fixa para o rótulo não variar conforme a ordem em
// que os projetos voltam do banco.
const GROUP_ORDER = ["Cultura", "Esporte", "Social"];

/**
 * Id da lei a partir do nome vindo da API (campo IncentiveLaws de
 * Campaign/FindBySubdomainAndOrProjectId, que devolve nome e não id).
 *
 * Casa por palavra-chave sem acento em vez de comparar a string inteira: o nome é um
 * registro editável do banco ("Estatuto de Criança e Adolescente" hoje, já foi "da"),
 * e um de/da trocado não pode derrubar o rótulo.
 */
export function incentiveLawIdFromName(name) {
  if (!name) return null;

  const n = String(name)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

  if (n.indexOf("esporte") >= 0) return 3;
  if (n.indexOf("cultura") >= 0 || n.indexOf("rouanet") >= 0) return 4;
  if (n.indexOf("idoso") >= 0) return 2;
  if (n.indexOf("crianc") >= 0 || n.indexOf("adolescente") >= 0 || n.indexOf("eca") === 0) return 1;

  return null;
}

/**
 * Rótulo para um conjunto de leis (ids). Devolve null quando não dá para dizer nada --
 * campanha sem projeto, lei fora das quatro cadastradas -- e aí quem chama mantém o
 * texto que o simulador já usava.
 */
export function incentiveLawLabel(lawIds) {
  const ids = (lawIds || []).map(Number).filter((id) => INCENTIVE_LAW_SHORT_LABEL[id]);
  const unique = ids.filter((id, i) => ids.indexOf(id) === i);

  if (unique.length === 0) return null;
  if (unique.length === 1) return INCENTIVE_LAW_SHORT_LABEL[unique[0]];

  const groups = GROUP_ORDER.filter((g) => unique.some((id) => INCENTIVE_LAW_GROUP[id] === g));

  // ECA + Idoso são leis diferentes mas o mesmo grupo: vira só "Social".
  if (groups.length === 1) return groups[0];

  return groups.slice(0, -1).join(", ") + " e " + groups[groups.length - 1];
}

/** Mesmo rótulo, a partir dos nomes das leis (formato da API). */
export function incentiveLawLabelFromNames(names) {
  return incentiveLawLabel((names || []).map(incentiveLawIdFromName).filter(Boolean));
}

export default incentiveLawLabel;
